import { UzButton, UzInput } from '@utazzo/ui';
import React, { useEffect, useState } from 'react';

import { type AppConfigEntryInput } from './settings.api';
import { useAppConfig, useUpdateAppConfig } from './use-settings';

/**
 * Tax & Pricing settings tab.
 *
 * Edits the two AppConfig keys consumed by the itinerary pricing step:
 *   - `tax.gstRatePercent` (number)
 *   - `tax.tcsRatePercent` (string - the legacy AppConfig schema stores it
 *     as a string because regulations have historically expressed it as
 *     "5%", "3%", etc. coming from a config text file).
 *
 * Both rates are snapshotted onto each new itinerary at create-time so an
 * edit here only affects itineraries created after the save. Existing
 * itineraries continue to display the rate they were quoted at.
 */

const Field = ({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}): React.JSX.Element => (
  <div>
    <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[12px] text-(--fg-faint)">{hint}</p>}
  </div>
);

export const TaxPricingTab = ({ canWrite = true }: { canWrite?: boolean }): React.JSX.Element => {
  const { data, isLoading } = useAppConfig();
  const save = useUpdateAppConfig();

  const [gst, setGst] = useState('');
  const [tcs, setTcs] = useState('');

  useEffect(() => {
    if (!data) return;
    setGst(String(data.tax.gstRatePercent));
    setTcs(data.tax.tcsRatePercent);
  }, [data]);

  if (isLoading || !data) {
    return <p className="p-6 text-sm text-(--fg-faint)">Loading…</p>;
  }

  const onSave = (): void => {
    const gstNum = Number(gst);
    if (!Number.isFinite(gstNum) || gstNum < 0) return;
    const entries: AppConfigEntryInput[] = [
      { key: 'tax.gstRatePercent', value: gstNum },
      // tcs stays a string so the existing AppConfig schema keeps validating
      // (it's a regex-loose string per the historical config file format).
      { key: 'tax.tcsRatePercent', value: tcs.trim() || '0' },
    ];
    save.mutate(entries);
  };

  const dirty =
    gst.trim() !== String(data.tax.gstRatePercent) ||
    tcs.trim() !== String(data.tax.tcsRatePercent);

  return (
    <div className="flex max-w-xl flex-col gap-6 py-6">
      <div className="rounded-md border border-(--border) bg-(--z-50) p-3 text-xs text-(--fg-muted)">
        Both GST and TCS apply to every itinerary, domestic and international. The rates below are
        snapshotted onto each new itinerary the moment its day-by-day plan is generated; older
        itineraries continue to show the rate they were quoted at.
      </div>

      <Field label="GST rate (%)" hint="Applied to the taxable amount on every new itinerary.">
        <UzInput
          inputMode="decimal"
          value={gst}
          onChange={(e) => setGst(e.target.value)}
          placeholder="e.g. 5"
        />
      </Field>

      <Field
        label="TCS rate (%)"
        hint="Applied alongside GST on every new itinerary. Stored as text to match the legacy AppConfig representation."
      >
        <UzInput
          inputMode="decimal"
          value={tcs}
          onChange={(e) => setTcs(e.target.value)}
          placeholder="e.g. 2"
        />
      </Field>

      <div className="flex items-center gap-3 pt-2">
        <UzButton onClick={onSave} disabled={!canWrite || !dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save tax settings'}
        </UzButton>
        {!canWrite && (
          <span className="text-xs text-(--fg-faint)">Read-only - you can view but not save.</span>
        )}
      </div>
    </div>
  );
};
